/**
 * Securities API Service
 * Frontend-Wrapper für alle Securities-bezogenen Tauri Commands
 */

import { core } from "@tauri-apps/api";

// ==================== TYPES ====================

export interface EnrichmentOptions {
  apiKey?: string;
  dividendScheduleLimit: number;
  includeDividendSchedule: boolean;
  includeMetrics: boolean;
  includePriceCache: boolean;
  includeReferenceData: boolean;
  isins: string[];
  priceCacheDays: number;
}

export interface SecuritiesEnrichmentResult {
  anzahl: number;
  erfolgreich: number;
  fehlgeschlagen: number;
  reference_data_complete: number;
  results: {
    securities: Record<string, null | SecurityEntry>;
  };
}

export interface SecurityEntry {
  dividend_schedule?: DividendData[];
  price_cache?: PriceData[];
  securities: Security;
  securities_metrics?: SecurityMetrics;
}

export interface Security {
  asset_type?: string;
  composite_figi: string;
  confidence: number;
  country?: string;
  description?: string;
  exchange_code: string;
  figi: string;
  final_yahoo_symbol: string;
  full_time_employees?: number;
  industry?: string;
  name: string;
  reference_data_complete?: boolean;
  region?: string;
  sector?: string;
  share_class_figi: string;
  ticker: string;
  validation_type: string;
  website?: string;
  yahoo_search_symbol?: string;
  yahoo_symbol: string;
}

export interface SecurityMetrics {
  avg_volume?: number;
  beta?: number;
  currency?: string;
  day_high?: number;
  day_low?: number;
  dividend_date?: string;
  dividend_rate?: number;
  dividend_yield?: number;
  eps?: number;
  ex_dividend_date?: string;
  fetched_at?: string;
  market_cap?: number;
  open_price?: number;
  pb_ratio?: number;
  pe_ratio?: number;
  previous_close?: number;
  price?: number;
  source?: string;
  volume?: number;
}

export interface PriceData {
  close: number;
  currency?: string;
  date: string;
  high?: number;
  low?: number;
  open?: number;
  volume?: number;
}

export interface DividendData {
  amount: number;
  currency?: string;
  date: string;
}

export interface SyncStats {
  dividends_inserted: number;
  errors: string[];
  metrics_inserted: number;
  price_cache_inserted: number;
  securities_inserted: number;
  securities_updated: number;
  total: number;
}

export type YahooReferenceData = Record<string, unknown>;

export type YahooMetrics = Record<string, unknown>;

export type EnrichmentResult = Record<string, unknown>;

export type TestTableResult = Record<string, unknown>;

// ==================== API FUNCTIONS ====================

/**
 * Sucht Yahoo-Symbol für eine ISIN
 *
 * @param isin
 *
 * @param timeout
 *
 * @returns {Promise<null | string>} Yahoo-Symbol oder null
 *
 */
export async function searchYahooSymbolByIsin(
  isin: string,
  timeout?: number,
): Promise<null | string> {
  return await core.invoke("search_yahoo_symbol_by_isin", { isin, timeout });
}

/**
 * Holt Referenzdaten für ein Symbol
 *
 * @param symbol
 *
 * @param isin
 *
 * @returns {Promise<YahooReferenceData>} Referenzdaten
 *
 */
export async function getYahooReferenceData(
  symbol: string,
  isin?: string,
): Promise<YahooReferenceData> {
  return await core.invoke("get_yahoo_reference_data", { isin, symbol });
}

/**
 * Holt Metriken für ein Symbol
 *
 * @param symbol
 *
 * @returns {Promise<YahooMetrics>} Metriken
 *
 */
export async function getYahooMetrics(symbol: string): Promise<YahooMetrics> {
  return await core.invoke("get_yahoo_metrics", { symbol });
}

/**
 * Holt historische Kursdaten
 *
 * @param symbol
 *
 * @param days
 *
 * @returns {Promise<null | PriceData[]>} Historische Kursdaten oder null
 *
 */
export async function getYahooPriceCache(
  symbol: string,
  days: number,
): Promise<null | PriceData[]> {
  return await core.invoke("get_yahoo_price_cache", { days, symbol });
}

/**
 * Holt Dividenden-Daten
 *
 * @param symbol
 *
 * @param limit
 *
 * @returns {Promise<DividendData[] | null>} Dividenden-Daten oder null
 *
 */
export async function getYahooDividends(
  symbol: string,
  limit: number,
): Promise<DividendData[] | null> {
  return await core.invoke("get_yahoo_dividends", { limit, symbol });
}

/**
 * Enrichiert ISINs mit OpenFIGI + Yahoo Finance
 *
 * @param options
 *
 * @returns {Promise<SecuritiesEnrichmentResult>} Enrichment-Ergebnis
 *
 */
export async function enrichSecuritiesOpenfigi(
  options: EnrichmentOptions,
): Promise<SecuritiesEnrichmentResult> {
  return await core.invoke("enrich_securities_openfigi", {
    apiKey: options.apiKey,
    dividendScheduleLimit: options.dividendScheduleLimit,
    includeDividendSchedule: options.includeDividendSchedule,
    includeMetrics: options.includeMetrics,
    includePriceCache: options.includePriceCache,
    includeReferenceData: options.includeReferenceData,
    isins: options.isins,
    priceCacheDays: options.priceCacheDays,
  });
}

/**
 * Erweitert bestehende Securities mit Referenzdaten
 *
 * @param isins
 *
 * @returns {Promise<EnrichmentResult>} Enrichment-Ergebnis
 *
 */
export async function enrichSecuritiesReferenceData(isins: string[]): Promise<EnrichmentResult> {
  return await core.invoke("enrich_securities_reference_data", { isins });
}

/**
 * Erweitert bestehende Securities mit Metriken
 *
 * @param isins
 *
 * @returns {Promise<EnrichmentResult>} Enrichment-Ergebnis
 *
 */
export async function enrichSecuritiesMetrics(isins: string[]): Promise<EnrichmentResult> {
  return await core.invoke("enrich_securities_metrics", { isins });
}

/**
 * Erweitert bestehende Securities mit Price Cache
 *
 * @param isins
 *
 * @param days
 *
 * @returns {Promise<EnrichmentResult>} Enrichment-Ergebnis
 *
 */
export async function enrichSecuritiesPriceCache(
  isins: string[],
  days: number,
): Promise<EnrichmentResult> {
  return await core.invoke("enrich_securities_price_cache", { days, isins });
}

/**
 * Erweitert bestehende Securities mit Dividenden
 *
 * @param isins
 *
 * @param limit
 *
 * @returns {Promise<EnrichmentResult>} Enrichment-Ergebnis
 *
 */
export async function enrichSecuritiesDividends(
  isins: string[],
  limit: number,
): Promise<EnrichmentResult> {
  return await core.invoke("enrich_securities_dividends", { isins, limit });
}

/**
 * Lädt ISINs aus JSON-Datei
 *
 * @param filePath
 *
 * @returns {Promise<string[]>} Array von ISINs
 *
 */
export async function loadIsinsFromFile(filePath: string): Promise<string[]> {
  return await core.invoke("load_isins_from_file", { filePath });
}

/**
 * Speichert Securities-Ergebnisse in JSON-Datei
 *
 * @param results
 *
 * @param filePath
 *
 * @returns {Promise<string>} Erfolgsmeldung
 *
 */
export async function saveSecuritiesToFile(
  results: SecuritiesEnrichmentResult,
  filePath: string,
): Promise<string> {
  return await core.invoke("save_securities_to_file", { filePath, results });
}

/**
 * Lädt Securities-Ergebnisse aus JSON-Datei
 *
 * @param filePath
 *
 * @returns {Promise<SecuritiesEnrichmentResult>} Securities-Ergebnisse
 *
 */
export async function loadSecuritiesFromFile(
  filePath: string,
): Promise<SecuritiesEnrichmentResult> {
  return await core.invoke("load_securities_from_file", { filePath });
}

/**
 * Testet Supabase-Verbindung
 *
 * @param url
 *
 * @param key
 *
 * @returns {Promise<{ success: boolean; message: string }>} Test-Ergebnis
 *
 */
export async function testSupabaseConnection(
  url?: string,
  key?: string,
): Promise<{ success: boolean; message: string }> {
  return await core.invoke("test_supabase_connection", { key, url });
}

/**
 * Testet Securities-Tabelle
 *
 * @param url
 *
 * @param key
 *
 * @returns {Promise<TestTableResult>} Test-Ergebnis
 *
 */
export async function testSecuritiesTable(url?: string, key?: string): Promise<TestTableResult> {
  return await core.invoke("test_securities_table", { key, url });
}

/**
 * Synchronisiert Securities zu Supabase
 *
 * @param results
 *
 * @param includeMetrics
 *
 * @param includePriceCache
 *
 * @param includeDividends
 *
 * @returns {Promise<SyncStats>} Synchronisations-Statistiken
 *
 */
export async function syncSecuritiesToSupabase(
  results: SecuritiesEnrichmentResult,
  includeMetrics = true,
  includePriceCache = false,
  includeDividends = false,
): Promise<SyncStats> {
  return await core.invoke("sync_securities_to_supabase", {
    includeDividends,
    includeMetrics,
    includePriceCache,
    results,
  });
}

/**
 * Lädt Securities aus Supabase
 *
 * @param isins
 *
 * @returns {Promise<SecuritiesEnrichmentResult>} Securities-Ergebnisse
 *
 */
export async function getSecuritiesFromSupabase(
  isins: string[],
): Promise<SecuritiesEnrichmentResult> {
  return await core.invoke("get_securities_from_supabase", { isins });
}
